export default customElements.define('w-textarea', class TextArea extends wealthify.CustomElementBaseClass {

    constructor () {
        const styleSelector = 'c-textarea';
        const config = {
            styleSelector: styleSelector,
            templateHTML: `
            <div class="${styleSelector}__wrapper" data-root>
                <div class="${styleSelector}__controls-container" data-controlscontainer>
                    <div class="${styleSelector}__input-container">
                        <textarea class=${styleSelector}__input data-input></textarea>
                    </div>
                    <span class=${styleSelector}__counter data-counter></span>
                </div>
                <span data-state class="u-display__hidden" aria-live="polite"></span>
                <w-icon data-icon class="${styleSelector}__icon"></w-icon>
            </div>`,
            cacheDom: [ 'data-controlscontainer', 'data-input', 'data-state', 'data-icon', 'data-counter' ]
        };

        super(config);

        this.onConnectedCallback = () => {
            this.classList.add(this.styleSelector);
            this.value = this.dom.input.value;
            this._updateAndStoreValues(this.dom.input, this);

            this.dom.input.addEventListener('blur', this._eventBindCallbacks.blur);
            this.dom.root.addEventListener('click', () => {
                this.dom.input.focus();
            });
            this.dom.input.addEventListener('change', this._eventBindCallbacks.keyup);
            this.dom.input.addEventListener('keyup', this._eventBindCallbacks.keyup);
        };
    }

    static get validWarnings () {
        return [
            'max',
            'min',
            'required'
        ];
    }

    static get observedProps () {
        return [
            new wealthify.Prop('key'),
            new wealthify.Prop('max', {type: wealthify.propTypes.Number()}),
            new wealthify.Prop('min', {type: wealthify.propTypes.Number()}),
            new wealthify.Prop('placeholderprop'),
            new wealthify.Prop('required', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('rows', { type: wealthify.propTypes.Number() }),
            new wealthify.Prop('showcounter', {
                default: false, 
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('value'),
            new wealthify.Prop('warnings', {
                type: wealthify.propTypes.Array(),
                validators: [wealthify.propValidators.IsInArray(this.validWarnings)]
            }),
            wealthify.props.Disabled(),
        ];
    }

    get propCallbacks () {
        return {
            disabledprop: (value) => {
                const classListAction = value ? 'add' : 'remove';
        
                this.dom.root.classList[classListAction](`${this.styleSelector}--disabled`);
                this.dom.input.disabled = value;
            },

            key: () => {
                this.dom.input.id = this.key;
                this.dom.input.name = this.key;
            },

            max: (value) => this._minMaxUpdate(value, 'max'),
        
            min: (value) => this._minMaxUpdate(value, 'min'),

            placeholderprop: (value) => {
                this.dom.input.placeholder = value;
            },
        
            required: (value) => {
                const isErrorAWarning = this._checkIsErrorAWarning('required');

                if (isErrorAWarning) {
                    return;
                }

                this.dom.input.required = value;
            },

            rows: (value) => {
                this.dom.input.rows = value;
            },

            showcounter: (value) => {
                if (this.max) {
                    const classListAction = value ? 'remove' : 'add';

                    this.dom.counter.classList[classListAction]('u-display__none');
                }
            },
        
            value: (value) => {
                if (value !== this.dom.input.value) {
                    this.dom.input.value = value;
                    this._updateAndStoreValues(this.dom.input, this);
                    this._dispatchEvent(this);
                    this._updateCounter();
                }
            },
            
            warnings: () => {}
        };
    }

    _minMaxUpdate (value, key) {
        const isErrorAWarning = this._checkIsErrorAWarning(key);

        if (isErrorAWarning) {
            return;
        }
        
        this.dom.input[`${key}Length`] = value;

        this._updateCounter();
    }

    _checkIsErrorAWarning (error) {
        return this.warnings !== undefined && wealthify.type.array.includes(error, this.warnings);
    }

    _eventBindCallbacks = {
        blur: () => {
            if (this.valid === true && this.hasWarning === false) {
                this.dom.icon.classList.remove(`${this.styleSelector}__icon--valid`);
                this.dom.controlscontainer.classList.remove(`${this.styleSelector}__controls-container--valid`);
            }
        },

        keyup: () => {
            this._updateState();
            this._updateCounter();
            this._dispatchEvent();
        }
    }

    _updateState () {
        const previouslyNotValid = this.valid === false || this.hasWarning === true;

        this._updateAndStoreValues();

        const classListInvalidAction = !this.valid ? 'add' : 'remove';
        const classListValidAction = this.valid && !this.hasWarning ? 'add' : 'remove';
        const classListWarningAction = this.hasWarning ? 'add' : 'remove';
        const icon = !this.valid || this.hasWarning ? 'danger-outlined' : 'success-outlined';

        const hasBeenCorrected = previouslyNotValid === true && this.valid === true;

        if (hasBeenCorrected || this.valid === false || this.hasWarning === true) {
            this.dom.icon.classList[classListInvalidAction](`${this.styleSelector}__icon--invalid`);
            this.dom.icon.classList[classListValidAction](`${this.styleSelector}__icon--valid`);
            this.dom.icon.classList[classListWarningAction](`${this.styleSelector}__icon--warning`);

            this.dom.controlscontainer.classList[classListInvalidAction](`${this.styleSelector}__controls-container--invalid`);
            this.dom.controlscontainer.classList[classListValidAction](`${this.styleSelector}__controls-container--valid`);
            this.dom.controlscontainer.classList[classListWarningAction](`${this.styleSelector}__controls-container--warning`);

            this.dom.icon.type = icon;
        }

        this.dom.state.textContent = this.valid ? 'valid' : 'invalid';
    }

    _updateCounter () {
        if (this.max) {
            const max = this.max;
            const chars = this.dom.input.value.length;

            this.dom.counter.innerHTML = `${chars} / ${max} characters`;
        }
    }

    _updateAndStoreValues () {
        this.value = this.dom.input.value;
        this.hasWarning = false;
        
        if (this.warnings) {
            this._checkAndSetWarning();
        } else {
            this.valid = this.dom.input.checkValidity();
        }
    }

    _checkAndSetWarning () {
        this._addWarningAttributes();

        this.valid = this.dom.input.checkValidity();

        const errorMap = {
            'tooShort': 'min',
            'tooLong': 'max',
            valueMissing: 'required',
        };

        for (let error in errorMap) {
            const currentErrors = this.dom.input.validity;
            const errorIsOverwritten = this.warnings.some((warning) => warning === errorMap[error]);

            if (currentErrors[error] && errorIsOverwritten) {
                this.hasWarning = true;
                this.valid = true;
            }
        }

        this._removeWarningAttributes();
    }

    _addWarningAttributes () {
        this.warnings.forEach((warning) => {
            this.dom.input.setAttribute(warning, this[warning]);
        });
    }

    _removeWarningAttributes () {
        this.warnings.forEach((warning) => {
            this.dom.input.removeAttribute(warning);
        });
    }

    _dispatchEvent () {
        const event = document.createEvent('Event');
        
        event.initEvent('valuechange', true, true);
        this.dispatchEvent(event);
    }
});